// Public modernisation ACCEPTANCE page (Session AEI, Director decision D1). No login: the
// unguessable projectAcceptToken IS the capability. This is where the branded acceptance-link
// email lands (/accept/project/:token). The customer reads the quotation AND the full Conditions
// of Contract, then clicks Sign and Accept, which triggers the Signable envelope.
//
// Visual treatment: the AcceptQuote world (the maintenance accept page is the platform's
// customer-facing gold standard). It reuses AcceptQuote.css for the chrome (the dark ELE header
// band above a light premium document column), with a small scoped supplement for the project
// pricing table and the Conditions-of-Contract typography.
//
// House rules: plain English, no personal names (ELE signs "For and on behalf of"), RFID never
// NFC, the single client phone number 0203 576 4760, pound symbol, no double hyphens.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PublicProjectAcceptView, ProjectWorksTermsClause } from "../../lib/api";
import "./AcceptQuote.css";
// Project-specific typography for the pricing table and the Conditions of Contract (a style element
// until Session ASB; the file's header says why it moved).
import "./AcceptProject.css";

const PHONE = "0203 576 4760";

function gbp(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_LABEL: Record<PublicProjectAcceptView["status"], string> = {
  awaiting_signature: "Awaiting signature",
  sent: "Sent for signing",
  signed: "Signed",
  expired: "Expired",
};

export function AcceptProject() {
  const { token = "" } = useParams();
  const [view, setView] = useState<PublicProjectAcceptView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<"not_found" | "error" | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Once the customer clicks Sign and Accept, drive the confirmation view from this so it shows
  // instantly without a reload (the envelope is created, the signing link emailed).
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadError(null);
    api.publicProjectAccept
      .get(token)
      .then((v) => {
        if (live) setView(v);
      })
      .catch((err) => {
        if (!live) return;
        setLoadError(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  const onAccept = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.publicProjectAccept.accept(token);
      setTriggered(true);
    } catch (err) {
      // ApiError.code carries the server's `message` (else `error`); the 410 and already_signed
      // cases switch the page state, everything else surfaces the message inline.
      if (err instanceof ApiError && err.status === 410) {
        setView((v) => (v ? { ...v, status: "expired", expired: true } : v));
      } else if (err instanceof ApiError && err.code === "already_signed") {
        setView((v) => (v ? { ...v, status: "signed", signed: true } : v));
      } else if (err instanceof ApiError && err.code && err.code !== err.name) {
        setSubmitError(err.code);
      } else {
        setSubmitError("Something went wrong. Please try again in a moment, or call the office and we will help.");
      }
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  const q = view?.quotation ?? null;
  const existing = useMemo(() => q?.existingInstall ?? [], [q]);
  const ex = q?.contractValueExVat ?? null;
  const vat = ex != null && q ? ex * q.vatRate : null;
  const inc = ex != null && vat != null ? ex + vat : null;
  const vatPct = q ? Math.round(q.vatRate * 100) : 20;

  if (loading) {
    return (
      <Shell>
        <div className="sec notice" role="status" aria-live="polite">
          <h2>Loading your quotation</h2>
          <p>Just a moment.</p>
        </div>
      </Shell>
    );
  }

  if (loadError || !view || !q) {
    return (
      <Shell>
        <div className="sec notice">
          <h2>{loadError === "not_found" ? "We could not find this quotation" : "Something went wrong"}</h2>
          <p>
            {loadError === "not_found"
              ? "This link may have expired or already been used. Please call the office and we will help."
              : "Please try again in a moment, or call the office and we will help."}
          </p>
          <p className="call">Call us on {PHONE}</p>
        </div>
      </Shell>
    );
  }

  const status = view.status;
  const secondName = view.secondSignatory?.name?.trim() || null;
  const secondRole = view.secondSignatory?.role?.trim() || null;

  return (
    <Shell>
      <div className="intro">
        <h1>Your Lift Works Quotation</h1>
        <p className="lead">
          Thank you for considering East London Elevators. Please review your quotation and the Conditions of Contract below, then sign to accept.
        </p>
      </div>

      <div className="strip">
        <div className="strip-cell">
          <div className="k">Prepared for</div>
          <div className="v">{q.clientName?.trim() || view.primarySignatoryName || "To be confirmed"}</div>
        </div>
        <div className="strip-cell">
          <div className="k">Site</div>
          <div className="v">{q.siteName?.trim() || "To be confirmed"}</div>
          {q.siteAddress ? <div className="s">{q.siteAddress}</div> : null}
        </div>
        <div className="strip-cell">
          <div className="k">Reference</div>
          <div className="v">{q.quoteRefDisplay ?? q.projectRef}</div>
          <div className="s">Prepared {q.dateDisplay}</div>
        </div>
        <div className="strip-cell">
          <div className="k">Status</div>
          <div className="v">
            <span className={`pj-pill pj-pill--${status}`}>{STATUS_LABEL[status]}</span>
          </div>
          <div className="s">{status === "expired" ? "" : `Valid until ${view.validUntilDisplay}`}</div>
        </div>
      </div>

      {/* Schedule of Works summary */}
      <div className="sec">
        <div className="sec-title">Schedule of Works</div>
        <p className="pj-title">
          <strong>{q.title}</strong>
          {q.typeLabel ? ` (${q.typeLabel})` : ""}
        </p>
        {q.scopeSummary?.trim() ? <Multiline text={q.scopeSummary} /> : <p className="sec-note">The scope of works is set out in the pricing below.</p>}
        {existing.length > 0 ? (
          <div className="pj-lifts">
            <div className="pj-sub">Lifts covered</div>
            <ul>
              {existing.map((r, i) => (
                <li key={i}>
                  <strong>{r.liftRef || "Lift"}</strong>
                  {r.type ? ` . ${r.type}` : ""}
                  {r.controller ? ` . ${r.controller}` : ""}
                </li>
              ))}
            </ul>
          </div>
        ) : q.liftsInScope != null ? (
          <p className="sec-note">
            {q.liftsInScope} lift{q.liftsInScope === 1 ? "" : "s"} in scope.
          </p>
        ) : null}
        {q.startDate ? (
          <p className="sec-note">
            <strong>Target commencement:</strong> {q.startDate}
          </p>
        ) : null}
      </div>

      {/* Price summary */}
      <div className="sec">
        <div className="sec-title">Price</div>
        {q.lineItems && q.lineItems.length > 0 ? (
          <table className="pj-table">
            <thead>
              <tr>
                <th>Element</th>
                <th>Scope</th>
                <th className="pj-num">Price (ex VAT)</th>
              </tr>
            </thead>
            <tbody>
              {q.lineItems.map((li, i) => (
                <tr key={i}>
                  <td>
                    {li.refLabel ? <span className="pj-ref">{li.refLabel}</span> : null}
                    {li.element || "-"}
                  </td>
                  <td>
                    {li.scopeDescription}
                    {li.notes ? <span className="pj-note">{li.notes}</span> : null}
                  </td>
                  <td className="pj-num">{gbp(li.priceExVat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {ex != null ? (
          <table className="pj-summary">
            <tbody>
              <tr>
                <td>Total (ex VAT)</td>
                <td className="pj-num">{gbp(ex)}</td>
              </tr>
              <tr>
                <td>VAT at {vatPct}%</td>
                <td className="pj-num">{gbp(vat!)}</td>
              </tr>
              <tr className="pj-grand">
                <td>Total (inc VAT)</td>
                <td className="pj-num">{gbp(inc!)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="sec-note">The price for these works is confirmed in writing separately.</p>
        )}

        {q.stages.length > 0 ? (
          <div className="pj-stages">
            <div className="pj-sub">Payment schedule</div>
            <table className="pj-table">
              <tbody>
                {q.stages.map((s, i) => (
                  <tr key={i}>
                    <td>{s.label}</td>
                    <td className="pj-num">{gbp(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="sec-note">Each stage is invoiced as it falls due, 30 day terms. VAT is added to each stage at the prevailing rate.</p>
          </div>
        ) : null}
      </div>

      {/* Quotation terms */}
      {view.quotationTerms.length > 0 ? (
        <div className="sec">
          <div className="sec-title">Quotation terms</div>
          <ul className="pj-bullets">
            {view.quotationTerms.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* The full Conditions of Contract, fully visible before the signature section. */}
      <div className="sec pj-terms">
        <div className="sec-title">Conditions of Contract</div>
        <h3 className="pj-h">Key terms</h3>
        <dl className="pj-keyterms">
          {view.terms.keyTerms.map((kt) => (
            <div key={kt.label}>
              <dt>{kt.label}</dt>
              <dd>{kt.text}</dd>
            </div>
          ))}
        </dl>
        <h3 className="pj-h">The Agreement</h3>
        {view.terms.agreementClauses.map((c) => (
          <Clause key={c.heading} clause={c} />
        ))}
        <h3 className="pj-h">Conditions of Contract</h3>
        {view.terms.conditions.map((c) => (
          <Clause key={c.heading} clause={c} />
        ))}
      </div>

      {/* Signature section / state */}
      {status === "signed" ? (
        <div className="sec pj-done">
          <div className="sec-title">This agreement has been signed</div>
          <p>
            Thank you. This agreement was signed{view.signedOnDisplay ? ` on ${view.signedOnDisplay}` : ""}. We will be in touch to arrange the works.
          </p>
          <p className="call">Any questions? Call us on {PHONE}</p>
        </div>
      ) : status === "expired" ? (
        <div className="sec notice">
          <div className="sec-title">This quotation has expired</div>
          <p>
            This quotation expired on {view.validUntilDisplay}. Please contact East London Elevators to discuss your project and we will prepare an
            up to date quotation.
          </p>
          <p className="call">Call us on {PHONE}</p>
        </div>
      ) : triggered || status === "sent" ? (
        <div className="sec pj-done">
          <div className="sec-title">Check your email to complete signing</div>
          <p>
            We have emailed you a secure signing link. Please open it to sign and complete your acceptance.
            {secondName ? ` Once you have signed, ${secondName}${secondRole ? ` (${secondRole})` : ""} will receive their signing request.` : ""}
          </p>
          <p className="call">Cannot find the email? Call us on {PHONE}</p>
        </div>
      ) : (
        <div className="sec summary">
          <div className="sec-title">Sign and Accept</div>
          <p>
            By clicking Sign and Accept below, you confirm you have read and agree to the Conditions of Contract set out above. This constitutes a
            binding contract between {q.clientName?.trim() || view.primarySignatoryName || "the Customer"} and East London Elevators Ltd.
          </p>
          {secondName ? (
            <div className="pj-signatories">
              <div className="pj-sub">Signatories</div>
              <ul>
                <li>{q.clientName?.trim() || view.primarySignatoryName || "The Customer"}</li>
                <li>
                  {secondName}
                  {secondRole ? ` (${secondRole})` : ""}
                </li>
              </ul>
              <p className="sec-note">Both parties will receive the signing request by email.</p>
            </div>
          ) : null}

          {submitError ? (
            <p role="alert" aria-live="assertive" className="cq-error">
              {submitError}
            </p>
          ) : null}

          <button type="button" onClick={onAccept} disabled={submitting} className="confirm">
            {submitting ? "Preparing your agreement..." : "Sign and Accept"}
          </button>
          <p className="fineprint">
            When you accept, we send your agreement to your email for signature. Nothing is charged yet.
          </p>
        </div>
      )}

      <Footer />
    </Shell>
  );
}

function Multiline({ text }: { text: string }) {
  return (
    <>
      {text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line, i) => (
          <p key={i} className="pj-p">
            {line}
          </p>
        ))}
    </>
  );
}

function Clause({ clause }: { clause: ProjectWorksTermsClause }) {
  return (
    <div className="pj-clause">
      <h4>{clause.heading}</h4>
      {clause.paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

// The page shell: the dark header band above the centred content column on the LIGHT canvas
// (shared verbatim with the maintenance accept page via AcceptQuote.css, which went dark in
// Session AGZ and back to light in Session AHD on the Director's decision -- this page has
// followed it both ways without an edit here, which is the point of sharing the stylesheet).
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="cq">
      <div className="cust-hdr">
        <div className="brand">
          <img className="brand-logo" src="/brand/ele-logo.png" alt="East London Elevators" />
          <small>Lift Works Agreement</small>
        </div>
        <div className="hdr-contact">
          <b>{PHONE}</b>
          <br />
          info@eastlondonelevators.co.uk
          <br />
          <a href="https://eastlondonelevators.co.uk" target="_blank" rel="noopener noreferrer">
            eastlondonelevators.co.uk
          </a>
        </div>
      </div>
      <div className="wrap">{children}</div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="ftr">
      <p>For and on behalf of East London Elevators.</p>
      <p className="co">East London Elevators Ltd, company 15429089, VAT GB493339461 | {PHONE} | info@eastlondonelevators.co.uk</p>
      <div className="cq-footbar cq-noprint">
        <p>East London Elevators Ltd &middot; Company No. 15429089 &middot; VAT GB493339461</p>
        <p>Unit 82a James Carter Road, Mildenhall, Bury St Edmunds, IP28 7DE</p>
        <p>
          <b>{PHONE}</b> &middot; <a href="mailto:info@eastlondonelevators.co.uk">info@eastlondonelevators.co.uk</a> &middot; eastlondonelevators.co.uk
        </p>
      </div>
    </footer>
  );
}
